import { resolveCaptionScript } from './captionLanguage';

import type { CaptionScript } from './captionLanguage';

/** The slice of the `language` model this module compares against. */
interface SupportedLanguageLike {
  value: string;
  script?: string | null;
  caption: string;
}

/**
 * PLAT-11412 — does a brand font file actually contain the glyphs a caption
 * script needs? Decided by probing a few representative characters through
 * the font's cmap (opentype.js `charToGlyphIndex`, 0 = .notdef): a script is
 * covered iff EVERY probe maps to a real glyph — deliberately conservative,
 * a font missing one common character is not caption-safe.
 *
 * Probes discriminate scripts that share ranges: kana for Japanese (kanji
 * overlap Chinese); PRC-only simplifications (absent from the Japanese
 * JIS X 0208 and Korean KS X 1001 sets) for zh-Hans; for zh-Hant, 妳/嗎 —
 * Big5-standard and everyday in Taiwanese text but outside JIS/KS, so a
 * Japanese font's kyūjitai or a Korean font's hanja can't false-positive
 * (體/點 alone would — they're old forms both sets carry). Latin probes are
 * plain ASCII so a symbol/display face fails too.
 */
export const SCRIPT_COVERAGE_PROBES: Record<CaptionScript, readonly string[]> =
  {
    latin: ['A', 'g', 'z', '0'],
    cyrillic: ['Б', 'ж', 'ы', 'Я'],
    japanese: ['あ', 'ぐ', 'ア', '語'],
    'chinese-simplified': ['简', '汉', '们', '这'],
    'chinese-traditional': ['體', '點', '妳', '嗎'],
    korean: ['한', '국', '어', '글'],
  };

/** The slice of an opentype.js Font this module needs. */
export interface FontGlyphLookup {
  charToGlyphIndex(char: string): number;
}

export type FontScriptCoverage = Record<CaptionScript, boolean>;

export const detectFontScriptCoverage = (
  font: FontGlyphLookup,
): FontScriptCoverage => {
  const coverage = {} as FontScriptCoverage;
  for (const [script, probes] of Object.entries(SCRIPT_COVERAGE_PROBES)) {
    coverage[script as CaptionScript] = probes.every(char => {
      try {
        return font.charToGlyphIndex(char) > 0;
      } catch {
        // A malformed cmap must degrade to "not covered", never throw the
        // upload away.
        return false;
      }
    });
  }
  return coverage;
};

/**
 * Family-level rule for warnings and picker flags: a script is missing only
 * when EVERY variant with KNOWN coverage misses it — if one weight covers
 * the script the family can serve it, and fonts uploaded before PLAT-11412
 * (no `scriptCoverage`) stay silent rather than falsely flagged.
 */
export const familyLacksScript = (
  variants: ReadonlyArray<{ scriptCoverage?: FontScriptCoverage }>,
  script: CaptionScript,
): boolean => {
  const known = variants.filter(v => v.scriptCoverage);
  if (!known.length) return false;
  // Strict `=== false` is load-bearing: a script added to CaptionScript
  // AFTER a font was scanned reads `undefined` from its persisted coverage
  // and must stay unflagged (unknown), not count as missing.
  return known.every(v => v.scriptCoverage?.[script] === false);
};

/**
 * The workspace's supported languages these variants cannot display, as
 * display captions ("Japanese"), deduped. Drives the upload warning toast
 * (single font = one variant) and the Brand Kit family-card line. The
 * language's own `script` field feeds `resolveCaptionScript`'s hint layer.
 */
export const getUncoveredWorkspaceLanguages = (
  variants: ReadonlyArray<{ scriptCoverage?: FontScriptCoverage }>,
  supportedLanguages: readonly SupportedLanguageLike[] | undefined,
): string[] => {
  if (!supportedLanguages?.length) return [];
  const missing = supportedLanguages.filter(l =>
    familyLacksScript(variants, resolveCaptionScript(l.value, l.script)),
  );
  return [...new Set(missing.map(l => l.caption))];
};
